import { noop } from 'lodash';
import { RefObject, useCallback, useContext, useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';

import { SkillDiscoveryItem as Item } from '@gmm/sdk-voodoo/skillDiscovery';
import { isSkillType, useMountedRef } from '@gmm/ui';
import { createNamedContext } from '~/lib/createNamedContext';

import { useSearchResults } from '../../useSearchResults';
import { useSkillDiscovery } from '../useSkillDiscovery';
import { UseTreeExpansion } from '../useTreeExpansion';

interface InViewProps {
  inView: boolean;
  item: Item;
  placement: 'above' | 'below';
}

type SetItemInView = (item: Item, options?: Omit<InViewProps, 'item'>) => void;

interface UseSearchHits {
  hitsAboveView: number;
  hitsBelowView: number;
  ref: RefObject<HTMLElement>;
  /** when options is omitted, it signals an unmount */
  setItemInView: SetItemInView;
}

export const useSearchHits = ({
  isExpanded,
}: Pick<UseTreeExpansion, 'isExpanded'>): UseSearchHits => {
  const { query, searchResults } = useSearchResults();
  const { data } = useSkillDiscovery();
  const ref = useRef<HTMLElement>(null);
  const batchRef = useRef(0);
  const mountedRef = useMountedRef();
  const pendingUpdates = useRef<Map<string, InViewProps | null>>(new Map());
  const [inView, setInView] = useImmer<Record<string, InViewProps>>({});

  const setItemInView = useCallback<SetItemInView>(
    (item, options): void => {
      window.cancelAnimationFrame(batchRef.current);
      pendingUpdates.current.set(
        item.id,
        options ? { ...options, item } : null,
      );
      batchRef.current = window.requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        setInView(draft => {
          pendingUpdates.current.forEach((options, id) => {
            if (!options) {
              delete draft[id];

              return;
            }

            draft[id] = options;
          });
          pendingUpdates.current.clear();
        });
      });
    },
    [mountedRef, setInView],
  );

  const { hitsAboveView, hitsBelowView } = useMemo(() => {
    if (!query || Object.keys(searchResults).length === 0 || !data) {
      return {
        hitsAboveView: 0,
        hitsBelowView: 0,
      };
    }

    const sumHits = (desiredPlacement: 'above' | 'below'): number => {
      return Object.entries(inView).reduce(
        (sum, [id, { inView, item, placement }]) => {
          const results = searchResults[id] ?? 0;

          if (!results || inView || placement !== desiredPlacement) return sum;

          if (!isSkillType(item) && isExpanded(item)) return sum;

          return sum + results;
        },
        0,
      );
    };

    return {
      hitsAboveView: sumHits('above'),
      hitsBelowView: sumHits('below'),
    };
  }, [data, inView, isExpanded, query, searchResults]);

  return { hitsAboveView, hitsBelowView, ref, setItemInView };
};

const SetItemInViewContext = createNamedContext<UseSearchHits['setItemInView']>(
  'InView',
  noop,
);

export const SetItemInViewProvider = SetItemInViewContext.Provider;

export const useSetItemInView = (): UseSearchHits['setItemInView'] =>
  useContext(SetItemInViewContext);
